// Capa cliente para suscripción a Web Push.
//
// Flujo:
//   1. ensurePushSubscription() comprueba estado del permiso, lo solicita
//      si está en "default", y se subscribe al PushManager con la VAPID
//      public key. Si todo va bien, manda los datos al backend.
//   2. El DeviceId es estable: se genera con crypto.randomUUID() la primera
//      vez y se guarda en localStorage. Sirve para que el backend
//      identifique este dispositivo concreto entre los del usuario.
//   3. Detecta plataforma para enviarla al backend (sirve para diagnóstico
//      y para futuras decisiones del sender — ej. payload distinto en iOS).

import { subscribePush, unsubscribePush } from './api';
import i18n from '../i18n';

// Traduce los motivos de indisponibilidad/errores de push con el idioma ACTIVO.
// Como pushAvailabilityReason() se recalcula en cada render del banner (que se
// re-renderiza al cambiar de idioma), el texto sigue el idioma seleccionado.
const pushReason = (key: string) => i18n.t(`components.push.reasons.${key}`);

const DEVICE_ID_KEY = 'PUSH_DEVICE_ID';
const SNOOZE_KEY = 'PUSH_PROMPT_SNOOZE_UNTIL';

// ── Helpers ────────────────────────────────────────────────────────────────

export function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (id) return id;
  // crypto.randomUUID está disponible en todos los navegadores modernos
  // (incluido iOS Safari 15+). Fallback simple para entornos antiguos.
  id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(DEVICE_ID_KEY, id);
  return id;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

// Detecta plataforma para mandarla al backend. El backend puede usar esto
// para tomar decisiones (ej. iOS no admite ciertas opciones de payload).
export function detectPlatform(): 'WebIos' | 'WebChromium' | 'WebDesktop' {
  const ua = navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua) || (ua.includes('mac') && 'ontouchend' in document);
  if (isIos) return 'WebIos';
  const isAndroid = /android/.test(ua);
  if (isAndroid) return 'WebChromium';
  return 'WebDesktop';
}

// iOS solo admite Web Push si la PWA está instalada en pantalla de inicio
// (iOS 16.4+). Devuelve un mensaje claro si el contexto no es viable.
export function pushAvailabilityReason(): { ok: true } | { ok: false; reason: string } {
  if (!isPushSupported()) {
    return { ok: false, reason: pushReason('unsupported') };
  }
  if (detectPlatform() === 'WebIos' && !isStandalone()) {
    return {
      ok: false,
      reason: pushReason('iosNeedsInstall'),
    };
  }
  return { ok: true };
}

// ── Snooze del banner ──────────────────────────────────────────────────────
// Mismo patrón que el InstallPrompt: si el usuario lo cierra con "Más tarde"
// volverá a aparecer automáticamente pasadas 2 HORAS (no queremos ser
// insistentes cada pocos minutos), sin esperar a una recarga.

export const PUSH_SNOOZE_MS = 2 * 60 * 60 * 1000;

export function snoozePushPrompt(): void {
  const until = Date.now() + PUSH_SNOOZE_MS;
  localStorage.setItem(SNOOZE_KEY, String(until));
}
export function isPushPromptSnoozed(): boolean {
  const v = localStorage.getItem(SNOOZE_KEY);
  if (!v) return false;
  return Date.now() < Number(v);
}
export function pushSnoozeMsRemaining(): number {
  const v = localStorage.getItem(SNOOZE_KEY);
  if (!v) return 0;
  return Math.max(0, Number(v) - Date.now());
}

// ── Subscripción ───────────────────────────────────────────────────────────

// Convierte la VAPID public key (base64url) a Uint8Array, formato que
// PushManager.subscribe() exige en `applicationServerKey`.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// `navigator.serviceWorker.ready` NUNCA resuelve si no hay un SW registrado (y
// tampoco rechaza), así que un `await` directo puede quedarse colgado PARA
// SIEMPRE: en dev con el SW desactivado, si el registro del SW falló, o en
// cualquier contexto sin SW. Lo envolvemos en una carrera con timeout para no
// bloquear nunca a quien lo use — en especial el logout, que espera a
// disablePushOnLogout() antes de limpiar la sesión.
async function getReadyRegistration(timeoutMs = 3000): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
  } catch {
    return null;
  }
}

// Estado actual de la subscripción del navegador (sin pedir permiso ni nada).
// La usamos para no spammear al usuario si ya tiene push activo.
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  try {
    const reg = await getReadyRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch { return null; }
}

// ── Unsubscribe (logout) ────────────────────────────────────────────────
//
// Tenemos dos helpers porque hay dos contextos:
//
//  - unsubscribeLocalPush(): solo invalida la subscripción del navegador
//    contra Mozilla/Google/Apple. Es lo único viable cuando el token JWT
//    ya está caducado (caso 401), porque sin token no podemos avisar al
//    back. Aun así, en cuanto el back intente enviar al SW invalidado
//    recibirá 410 Gone y el sender ya marca IsActive=false solo. Así
//    queda cubierto el caso del 401.
//
//  - disablePushOnLogout(): hace lo anterior + avisa al back vía
//    /api/PushNotifications/Unsubscribe para que actualice IsActive en BD
//    inmediatamente, sin esperar al primer broadcast fallido. Es la ruta
//    que se llama desde el botón "Cerrar sesión" del usuario.
//
// Ambos son best-effort: cualquier error se traga porque NO debe bloquear
// el logout. Lo peor que puede pasar es que la fila quede unos minutos
// como activa y se desactive sola al primer envío.

export async function unsubscribeLocalPush(): Promise<void> {
  try {
    const reg = await getReadyRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  } catch { /* best-effort */ }
}

// Re-subscribe silenciosa tras un login. Se llama en cada cambio a
// "autenticado=true" (incluido el primer arranque con sesión guardada).
//
// La idea: el permiso del navegador (`Notification.permission`) NO se borra
// al hacer logout; solo borramos la subscripción del servidor push. Por
// tanto, si el user ya había aceptado push antes, podemos re-subscribirlo
// sin prompt — `enablePushNotifications` no muestra nada cuando el permiso
// ya está concedido. Si el permiso está "default" o "denied" no hacemos
// nada y se mantiene el flujo manual del banner.
export async function resubscribeIfPermissionGranted(): Promise<void> {
  try {
    if (!isPushSupported()) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    const avail = pushAvailabilityReason();
    if (!avail.ok) return;
    await enablePushNotifications();
  } catch { /* best-effort: no bloqueamos el login si esto falla */ }
}

export async function disablePushOnLogout(): Promise<void> {
  // 1. Avisar al back. El email lo saca del JWT — solo damos de baja la
  //    fila de este dispositivo (DeviceId estable en localStorage).
  try {
    await unsubscribePush({ deviceId: getOrCreateDeviceId() });
  } catch { /* best-effort: red caída, 5xx, etc. — el SW unsubscribe local
                lo cubre vía 410 Gone en el siguiente broadcast. */ }

  // 2. Invalidar la subscripción local: esto es lo que evita que el SW
  //    siga mostrando notificaciones en este dispositivo aunque el back
  //    no se haya enterado.
  await unsubscribeLocalPush();
}

// Pide permiso al usuario y, si lo concede, se suscribe + manda al backend.
// Si ya estaba suscrito, igualmente lo reenvía al backend (refresca tokens).
// Devuelve `true` si quedó suscrito al final del proceso.
export async function enablePushNotifications(): Promise<{ ok: boolean; reason?: string }> {
  const avail = pushAvailabilityReason();
  if (!avail.ok) return { ok: false, reason: avail.reason };

  if (!__VAPID_PUBLIC_KEY__) {
    return { ok: false, reason: pushReason('vapidMissing') };
  }

  // Pedir permiso al usuario. Si ya lo tiene concedido, devuelve "granted"
  // sin mostrar prompt.
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return { ok: false, reason: pushReason('permissionDenied') };
  }

  try {
    const reg = await getReadyRegistration();
    if (!reg) return { ok: false, reason: pushReason('swUnavailable') };
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // El cast a BufferSource evita un mismatch entre Uint8Array<ArrayBufferLike>
      // y BufferSource que TS marca como incompatible aun siendo el mismo dato.
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(__VAPID_PUBLIC_KEY__) as unknown as BufferSource,
      });
    }

    const payload = {
      deviceId: getOrCreateDeviceId(),
      platform: detectPlatform(),
      endpoint: sub.endpoint,
      p256dh: bufferToBase64Url(sub.getKey('p256dh')),
      auth: bufferToBase64Url(sub.getKey('auth')),
      userAgent: navigator.userAgent,
      appVersion: __APP_VERSION__,
    };
    await subscribePush(payload);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: (e as Error)?.message ?? pushReason('unknownError') };
  }
}
